import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — MUA: BẢNG KÊ THẦU PHỤ · HÓA ĐƠN MUA · PHẢI TRẢ (F17) ═══════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/ap/*`.
 *
 * ⚠ MỌI cửa ở đây chạm GIÁ VỐN (tiền trả thầu phụ). Quyền siết ở `@rs/ap`; lược đồ chỉ lo hình
 *   dạng dữ liệu.
 */

/** "Loại NCC" của bản cũ (`APVendorType`, `0110`). */
export const AP_VENDOR_TYPE_VALUES = ['subcon', 'fuel', 'maintenance', 'vetc', 'salary', 'other'] as const

export const CreateApStatementBody = z.object({
  costIds: z.array(Uuid).min(1, 'Chưa chọn khoản chi phí nào để lập bảng kê.'),
  note: blankAsAbsent(z.string()),
})

export const WaitingCostQuery = z.object({ vendorId: blankAsAbsent(Uuid) })

export const ListApStatementQuery = PageQuery.extend({
  vendorId: blankAsAbsent(Uuid),
  status: blankAsAbsent(z.enum(['draft', 'confirmed', 'posted', 'cancelled'])),
})

/** 🔗 Tra bảng kê theo MÃ (link sâu `?stmt=`) — mọi loại bảng kê, không chỉ thầu phụ. */
export const ApStatementRefQuery = z.object({ ref: z.string().trim().min(1).max(64) })

/**
 * ══ BỘ LỌC CỦA SỔ CHỨNG TỪ MUA — TẤT CẢ chạy ở MÁY CHỦ (§8.3) ════════════════════════════════
 * Bản song sinh của sổ bán (`apiAr.ListInvoiceQuery`); xem lý lẽ đầy đủ ở đó.
 */
export const ListApInvoiceQuery = PageQuery.extend({
  vendorId: blankAsAbsent(Uuid),
  status: blankAsAbsent(z.enum(['posted', 'partial', 'paid', 'cancelled'])),
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
  /** Ô 🔍 — tên NCC · mã NCC · MST. */
  q: z.string().trim().min(1).optional(),
  /** Loại đối tượng trả tiền (NCC · khách · nhân viên…). */
  vtype: z.string().trim().min(1).optional(),
  /** Tiền tố TK phải trả, VD `331`. Phiếu chưa khai TK nào coi như `331` (bản cũ). */
  tk: z.string().trim().min(1).optional(),
  /** Đã có hóa đơn đỏ — số ở đầu phiếu HOẶC ở một dòng. */
  inv: blankAsAbsent(z.enum(['yes', 'no'])),
  /** Đã nạp MISA (đẩy API hoặc xuất Excel) hay chưa. */
  amis: blankAsAbsent(z.enum(['yes', 'no'])),
  /** Chứng từ chỉ để quản trị. */
  dx: blankAsAbsent(z.enum(['yes', 'no'])),
})

/** Cửa TÓM TẮT không lật trang — nó cộng và đếm. */
export const ApRegisterSummaryQuery = ListApInvoiceQuery.omit({ limit: true, cursor: true, dir: true })
export type ApRegisterSummaryQueryT = z.infer<typeof ApRegisterSummaryQuery>

/**
 * 🧾 Sửa phiếu chứng từ mua — CHỈ phần không đụng tiền (`ApDocTab`). `null` = xoá giá trị, vắng khoá = không đụng.
 * ⚠ `confirmPushed`: chứng từ đã đẩy AMIS, người sửa đã được hỏi lại.
 */
export const UpdateApDocBody = z.object({
  version: Version,
  docDate: blankAsAbsent(ISODate),
  postDate: blankAsAbsent(ISODate),
  note: z.string().max(1000).nullable().optional(),
  lines: z.array(z.object({
    id: Uuid,
    itemCode: z.string().max(100).nullable().optional(),
    label: z.string().max(500).optional(),
    invoiceNo: z.string().max(100).nullable().optional(),
    invoiceDate: blankAsAbsent(ISODate.nullable()),
    amisServiceCode: z.string().max(100).nullable().optional(),
    acctExpense: z.string().max(20).nullable().optional(),
    acctPayable: z.string().max(20).nullable().optional(),
    acctVat: z.string().max(20).nullable().optional(),
    expenseItem: z.string().max(100).nullable().optional(),
  })).max(500).optional(),
  vendorType: blankAsAbsent(z.enum(AP_VENDOR_TYPE_VALUES)),
  paymentMethod: blankAsAbsent(z.enum(['bank', 'cash', 'both'])),
  creditDays: blankAsAbsent(z.number().int().nonnegative().nullable()),
  dueDate: blankAsAbsent(ISODate.nullable()),
  bankAccountId: blankAsAbsent(Uuid.nullable()),
  invoiceNo: z.string().max(100).nullable().optional(),
  invoiceDate: blankAsAbsent(ISODate.nullable()),
  invoiceSymbol: z.string().max(50).nullable().optional(),
  invoiceFormNo: z.string().max(50).nullable().optional(),
  confirmPushed: z.boolean().optional(),
})
export type UpdateApDocBodyT = z.infer<typeof UpdateApDocBody>

/** 🧾 Nhận hóa đơn NCC cho chứng từ đã ghi sổ (`ReceiveInvoiceModal`). */
export const ReceiveApInvoiceBody = z.object({
  version: Version,
  invoiceNo: z.string().trim().min(1, 'Nhập số hóa đơn.').max(100),
  invoiceDate: ISODate,
  invoiceSymbol: z.string().max(50).optional(),
  confirmPushed: z.boolean().optional(),
})
export type ReceiveApInvoiceBodyT = z.infer<typeof ReceiveApInvoiceBody>

/** 🚀 Đẩy AMIS một hay nhiều chứng từ mua — mỗi chứng từ một chứng từ kế toán, chung một lượt bấm. */
export const PushApAmisBody = z.object({ ids: z.array(Uuid).min(1, 'Chưa chọn chứng từ nào.').max(200) })
export type PushApAmisBodyT = z.infer<typeof PushApAmisBody>

export const ApDebtQuery = z.object({
  asOf: blankAsAbsent(ISODate),
  vendorId: blankAsAbsent(Uuid),
})

export const ApIdParams = z.object({ id: Uuid })

/* ══ ĐỐI SOÁT THẦU PHỤ (F21) ═════════════════════════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG CÓ `vendorId` TRONG THÂN. Thầu phụ gửi đề xuất cho CHÍNH MÌNH, và `vendorId` lấy từ
 *   phiên. Để nó ở đây là mở đường cho một thầu phụ ký tên công ty khác lên đề xuất của mình —
 *   lược đồ là chỗ chặn rẻ nhất, chặn bằng cách KHÔNG NHẬN.
 */
export const SendReconBody = z.object({
  jobRefs: z.array(z.string().min(1)).max(200, 'Tối đa 200 mã đơn một đề xuất.').default([]),
  items: z.array(z.object({
    label: z.string().min(1, 'Mỗi dòng phải có tên khoản.'),
    amount: moneyField('Số tiền'),
  })).min(1, 'Chưa kê khoản nào.').max(100, 'Tối đa 100 dòng một đề xuất.'),
  note: blankAsAbsent(z.string()),
})

export const AckInvoiceBody = z.object({ version: Version })

export const ReviewReconBody = z.object({
  version: Version,
  replyNote: blankAsAbsent(z.string()),
})

export const ListReconQuery = PageQuery.extend({
  vendorId: blankAsAbsent(Uuid),
  status: blankAsAbsent(z.enum(['pending', 'reviewed'])),
})

/* ══ BẢNG KÊ CHI PHÍ ĐƠN HÀNG (`0098`, tab Chi phí khác ĐH) ═══════════════════════════════════════════ */

export const JobFeeBasis = z.enum(['invoice', 'pickup', 'deliver'])

export const JobFeePendingQuery = z.object({
  basis: JobFeeBasis.default('invoice'),
  from: z.union([ISODate, z.literal('')]).default(''),
  to: z.union([ISODate, z.literal('')]).default(''),
  label: blankAsAbsent(z.string()),
})

export const CreateJobFeeStatementBody = z.object({
  items: z.array(z.object({ jobId: Uuid, label: z.string().min(1) })).min(1, 'Chưa chọn khoản nào.').max(500),
  payee: z.object({ kind: z.enum(['vendor', 'customer', 'staff']), id: Uuid }).optional(),
  invoiceNo: blankAsAbsent(z.string()),
  invoiceDate: blankAsAbsent(ISODate),
  vatRate: blankAsAbsent(z.coerce.number().min(0).max(100)),
  mgmtOnly: z.boolean().optional(),
})

export const CloseJobFeePeriodBody = z.object({
  basis: JobFeeBasis,
  from: z.union([ISODate, z.literal('')]),
  to: z.union([ISODate, z.literal('')]),
  label: blankAsAbsent(z.string()),
})

export const JobFeeJobBody = z.object({ version: Version, jobId: Uuid })

export const PostJobFeeStatementBody = z.object({
  version: Version,
  postDate: blankAsAbsent(ISODate),
  invoiceNo: blankAsAbsent(z.string()),
  invoiceDate: blankAsAbsent(ISODate),
  vatRate: blankAsAbsent(z.coerce.number().min(0).max(100)),
})

export const JobFeeReasonBody = z.object({ version: Version, reason: z.string().default('') })

/** 👁 Xem trước ghi sổ HÀNG LOẠT bảng kê chi phí đơn hàng (L47) — số chứng từ dự kiến + tổng tiền theo ngày ghi sổ. */
export const JobFeeBulkPostPreviewBody = z.object({
  ids: z.array(Uuid).min(1).max(200),
  postDate: ISODate,
})
export type JobFeeBulkPostPreviewBodyT = z.infer<typeof JobFeeBulkPostPreviewBody>

export type JobFeePendingQueryT = z.infer<typeof JobFeePendingQuery>
export type CreateJobFeeStatementBodyT = z.infer<typeof CreateJobFeeStatementBody>
export type CloseJobFeePeriodBodyT = z.infer<typeof CloseJobFeePeriodBody>
export type JobFeeJobBodyT = z.infer<typeof JobFeeJobBody>
/**
 * 📝 TRÌNH CHI một bảng kê chi phí đơn hàng (rà Lô 3, 17/10) — cửa riêng của bảng kê (`apStatement:write`), không qua
 * `/dntt`. Số tiền · đối tượng · chứng từ lấy từ chứng từ mua của bảng kê; người lập chỉ gửi diễn giải + STK nhận.
 */
export const JobFeePayRequestBody = z.object({
  category: blankAsAbsent(z.string().max(500)),
  description: blankAsAbsent(z.string().max(2000)),
  expenseItem: blankAsAbsent(z.string().max(200)),
  payeeCode: blankAsAbsent(z.string().max(50)),
  payeeAccount: blankAsAbsent(z.string().max(50)),
  payeeBank: blankAsAbsent(z.string().max(200)),
  payeeHolder: blankAsAbsent(z.string().max(200)),
}).strict()
export type JobFeePayRequestBodyT = z.infer<typeof JobFeePayRequestBody>
export type PostJobFeeStatementBodyT = z.infer<typeof PostJobFeeStatementBody>
export type JobFeeReasonBodyT = z.infer<typeof JobFeeReasonBody>

export type CreateApStatementBodyT = z.infer<typeof CreateApStatementBody>
export type ListApStatementQueryT = z.infer<typeof ListApStatementQuery>
export type ApStatementRefQueryT = z.infer<typeof ApStatementRefQuery>
export type ListApInvoiceQueryT = z.infer<typeof ListApInvoiceQuery>
export type ApDebtQueryT = z.infer<typeof ApDebtQuery>
export type SendReconBodyT = z.infer<typeof SendReconBody>
export type ListReconQueryT = z.infer<typeof ListReconQuery>

/* ══ 📋 TAB BẢNG KÊ NCC (`PurchaseDocsTab`) ══════════════════════════════════════════════════════════════════════ */

/** Khoảng NGÀY CHẠY của đơn (mặc định 30 ngày gần nhất ở màn — user 27/07). Trống = mọi ngày. */
export const VendorStmtRegisterQuery = z.object({
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
})
export type VendorStmtRegisterQueryT = z.infer<typeof VendorStmtRegisterQuery>

/** Xem một nhóm "chưa lập bảng kê" — các khoản cùng một thầu phụ. */
export const VendorStmtPreviewBody = z.object({ costIds: z.array(Uuid).min(1, 'Chưa chọn khoản nào.').max(2000) })
export type VendorStmtPreviewBodyT = z.infer<typeof VendorStmtPreviewBody>

/** Hóa đơn NCC nhập trước khi ghi sổ — số trống = xoá hóa đơn đã nhập. */
export const VendorStmtInvoiceBody = z.object({
  version: Version,
  invoiceNo: blankAsAbsent(z.string().max(100)),
  invoiceDate: blankAsAbsent(ISODate),
  invoiceSymbol: blankAsAbsent(z.string().max(50)),
})
export type VendorStmtInvoiceBodyT = z.infer<typeof VendorStmtInvoiceBody>

export const VendorStmtMgmtBody = z.object({ version: Version, mgmtOnly: z.boolean() })
export type VendorStmtMgmtBodyT = z.infer<typeof VendorStmtMgmtBody>

const VatByJob = z.record(Uuid, z.number().min(0).max(100))

export const VendorStmtVatBody = z.object({ version: Version, vatByJob: VatByJob })
export type VendorStmtVatBodyT = z.infer<typeof VendorStmtVatBody>

/** ✓ Kế toán xác nhận + ghi sổ (một bước). Số chứng từ trống = tự nhảy theo nhóm MDV / DXMDV. */
export const ConfirmPostVendorStmtBody = z.object({
  version: Version,
  postDate: blankAsAbsent(ISODate),
  apDocNo: blankAsAbsent(z.string().max(30)),
  expenseItem: blankAsAbsent(z.string().max(50)),
  mgmtOnly: z.boolean().default(false),
  vatByJob: VatByJob.default({}),
  invoiceNo: blankAsAbsent(z.string().max(100)),
  invoiceDate: blankAsAbsent(ISODate),
  invoiceSymbol: blankAsAbsent(z.string().max(50)),
})
export type ConfirmPostVendorStmtBodyT = z.infer<typeof ConfirmPostVendorStmtBody>

/**
 * BKS (theo chặng) · số cont · số hiệu toa · NGÀY THỰC HIỆN (theo đơn) sửa trên bảng kê — ghi thẳng xuống đơn / chặng.
 * Chỉ nhận id nằm trong bảng kê; khoá khác bị bỏ qua.
 */
export const VendorStmtTransportBody = z.object({
  plates: z.record(Uuid, z.string().max(30)).default({}),
  conts: z.record(Uuid, z.string().max(30)).default({}),
  wagons: z.record(Uuid, z.string().max(30)).default({}),
  dates: z.record(Uuid, ISODate).default({}),
})
export type VendorStmtTransportBodyT = z.infer<typeof VendorStmtTransportBody>

export const VendorStmtReasonBody = z.object({ version: Version, reason: z.string().max(500).default('') })
export type VendorStmtReasonBodyT = z.infer<typeof VendorStmtReasonBody>

/** 📝 Lập ĐNTT cho bảng kê — đã ghi sổ thì theo chứng từ mua, chưa ghi sổ thì lập SỚM theo mã bảng kê. */
export const VendorStmtPayRequestBody = z.object({}).strict()

/* ══ 💜 BẢNG KÊ CHI HỘ KHÁCH (`OnBehalfStmtTab` vế chi) ══════════════════════════════════════════════════════════ */

export const CreateOnBehalfStmtBody = z.object({
  customerId: Uuid,
  jobIds: z.array(Uuid).min(1, 'Chưa chọn đơn nào.').max(500),
})
export type CreateOnBehalfStmtBodyT = z.infer<typeof CreateOnBehalfStmtBody>

/** Đối tượng phải trả 33885 — `null` = bỏ trống (chưa chọn NCC). */
export const OnBehalfVendorBody = z.object({ version: Version, vendorId: Uuid.nullable() })
export type OnBehalfVendorBodyT = z.infer<typeof OnBehalfVendorBody>

export const PostOnBehalfStmtBody = z.object({ version: Version, postDate: blankAsAbsent(ISODate) })
export type PostOnBehalfStmtBodyT = z.infer<typeof PostOnBehalfStmtBody>

/* ══ 💳 CÔNG NỢ PHẢI TRẢ — chứng từ tạo tay · sửa dòng · số dư đầu kỳ (`0110`, `AP.tsx`) ═══════════════════════════ */

const ApManualLine = z.object({
  label: z.string().trim().min(1, 'Dòng thiếu tên hàng / dịch vụ.').max(500),
  quantity: z.number().positive(),
  priceVnd: z.number().nonnegative(),
  vatRate: z.number().min(0).max(100),
  unit: blankAsAbsent(z.string().max(50)),
  itemCode: blankAsAbsent(z.string().max(100)),
  acctExpense: blankAsAbsent(z.string().max(20)),
  acctPayable: blankAsAbsent(z.string().max(20)),
  acctVat: blankAsAbsent(z.string().max(20)),
  expenseItem: blankAsAbsent(z.string().max(100)),
})

export const ManualApInvoiceBody = z.object({
  vendorId: Uuid,
  vendorType: z.enum(AP_VENDOR_TYPE_VALUES),
  docDate: blankAsAbsent(ISODate),
  postDate: blankAsAbsent(ISODate),
  dueDate: blankAsAbsent(ISODate),
  creditDays: blankAsAbsent(z.number().int().nonnegative()),
  paymentMethod: blankAsAbsent(z.enum(['bank', 'cash', 'both'])),
  bankAccountId: blankAsAbsent(Uuid),
  invoiceFormNo: blankAsAbsent(z.string().max(50)),
  invoiceSymbol: blankAsAbsent(z.string().max(50)),
  invoiceNo: blankAsAbsent(z.string().max(100)),
  invoiceDate: blankAsAbsent(ISODate),
  note: blankAsAbsent(z.string().max(1000)),
  lines: z.array(ApManualLine).min(1).max(100),
})
export type ManualApInvoiceBodyT = z.infer<typeof ManualApInvoiceBody>

export const ReplaceApLinesBody = z.object({
  version: Version,
  lines: z.array(ApManualLine).min(1).max(100),
  confirmPushed: z.boolean().optional(),
})
export type ReplaceApLinesBodyT = z.infer<typeof ReplaceApLinesBody>

export const OpeningApImportBody = z.object({
  asOf: ISODate,
  vendorType: z.enum(AP_VENDOR_TYPE_VALUES),
  rows: z.array(z.object({
    name: z.string().trim().min(1).max(300),
    tax: blankAsAbsent(z.string().max(50)),
    amountVnd: z.number().positive(),
    dueDate: blankAsAbsent(ISODate),
    note: blankAsAbsent(z.string().max(500)),
  })).min(1).max(500),
})
export type OpeningApImportBodyT = z.infer<typeof OpeningApImportBody>
